import React, {useEffect, useState, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, Link} from 'react-router-dom';
import PageBase from "../../../components/PageBase/index";
import Field from "../../../components/FieldGroup/Field/index";
import bullet from '../../../assets/title-bullet.png';
import * as heatmapActions from "../../../redux/Heatmap/actions";
import * as ROUTES from "../../../utils/routes";
import FieldGroup from "../../../components/FieldGroup/index";

import './ViewHeatmap.scss';
import ObjectSummaryRow from "../../../components/ObjectSummaryRow";
import Button from "../../../components/Button";
import PopupWin from "../../../components/PopupWin";
import PopupButton from "../../../components/PopupButton";
import EditOpportunity from "../../Opportunities/EditOpportunity";
import EditHeatmap from "../EditHeatmap";
import * as opportunityActions from "../../../redux/Opportunity/actions";

const ViewHeatmap = props => {
	const [loading, setLoading] = useState(true);
	const [editHeatmap, setEditHeatmap] = useState(null);
	const editHeatmapRef = useRef(null);
	const dispatch = useDispatch();
	const params = useParams();
	const heatmap = useSelector(state => state.heatmap.heatmaps?.find(h => h?.Id === params.id));

	useEffect(() => {
		const load = async () => {
			if(heatmap == null && params.id) {
				dispatch(heatmapActions.loadHeatmapById(params.id))
			}
		};
		load();
	}, [heatmap, params.id]);

	const editHeatmapFooter = useMemo(() => (
		<PopupButton
			variant="primary"
			onClick={() => editHeatmapRef.current?.save()}
		>
			Save
		</PopupButton>
	), []);

	if(heatmap == null) {
		return <div/>;
	}

	const onHeatmapSaved = async () => {
		setEditHeatmap(null);
		setLoading(true);
		await dispatch(heatmapActions.loadHeatmapById(params.id))
		setLoading(false);
	};

	const summary = [
		{ title: 'Account Name', value: heatmap.Related_Account__r.Name, blue: true},
		{ title: 'Account Name (Contact)', value: heatmap.Contact__r?.Name},
		{ title: 'Request Reason', value: heatmap.Request_Reason__c},
		{ title: 'Engagement Status', value: heatmap.Relationship_Strength__c},
		{ title: 'Target Date', value: heatmap.Target_Date__c},
		{ title: 'Touch Base Date', value: heatmap.Touch_Base_Date__c}
	];

	return (
		<PageBase title="Heatmap"
		          removeMargins
		          bodyBg="#fff"
		          headerLinkText="All Heatmaps"
		          headerLinkTarget={ROUTES.HEATMAPS}
		>
			<PopupWin
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={editHeatmap != null}
				onClose={() => setEditHeatmap(null)}
				title="Heatmap"
				titleStyle={{fontSize: '18px', margin: '0 0 25px 0'}}
				contentStyle={{width: '100%', height: '90%', padding: 0}}
				popupStyle={{width: '90%', maxWidth: '1600px', height: '84%', maxHeight: '800px', overflow: 'none'}}
				footerStyle={{borderTop: '1px solid #D3E1ED'}}
				footer={editHeatmapFooter}>
				{ editHeatmap && <EditHeatmap ref={editHeatmapRef} editMode={true} heatmap={editHeatmap} onClose={onHeatmapSaved} /> }
			</PopupWin>

			<div className="view-heatmap-title">
				<img src={bullet} style={{width: '24px', marginRight: '20px'}} />
				<span>{heatmap.Name}</span>

				<div className="heatmap-header-buttons">
					<Button onClick={() => setEditHeatmap(heatmap)} type="blue" center>Edit Heatmap</Button>
				</div>
			</div>

			<ObjectSummaryRow data={summary} />

			<div style={{height: '100%', overflowY: 'auto', padding: '0 20px'}}>
				<FieldGroup title="Details" expandable expanded>
					<Field title="Heatmap Owner" highlight>{heatmap.Owner_First_Name__c}</Field>
					<Field title="Heatmap Name">{heatmap.Name}</Field>

					<Field title="Account Name">{heatmap.Related_Account__r.Name}</Field>
					<Field title="Contact">{heatmap.Contact__r?.Name}</Field>

					<Field title="Priority">{heatmap.Priority__c}</Field>
					<Field title="Preferred Channel">{heatmap.Preferred_Channel__c}</Field>

					<Field title="Intro By">{heatmap.Intro_By__r?.Name}</Field>
					<Field title="Engagement Status">{heatmap.Engagement_Status__c}</Field>

					<Field title="Relationship Strength">{heatmap.Relationship_Strength__c}</Field>
					<Field title="Campaign">{heatmap.Campaign__c}</Field>

					<Field title="Description">{heatmap.Description__c}</Field>
				</FieldGroup>

				<FieldGroup title="Communication Timing" expandable>
					<Field title="Target Date">{heatmap.Target_Date__c}</Field>
					<Field title="Touch Base Date">{heatmap.Touch_Base_Date__c}</Field>

					<Field title="Communication Interval">{heatmap.Communication_Interval__c}</Field>
					<Field title="Touchbase Timing">{heatmap.Touchbase_Timing__c}</Field>

					<Field title="Last Touchbase">{heatmap.Last_Touchbase__c}</Field>
					<Field title="Next Touchbase">{heatmap.Next_Touchbase__c}</Field>
				</FieldGroup>

				<FieldGroup title="Activity" expandable>
					<Field title="Next Step">{heatmap.Next_Step__c}</Field>
					<Field title="Next Step Date">{heatmap.Next_Step_Date__c}</Field>

					<Field title="Next Step POC">{heatmap.Next_Step_POC__r?.Name}</Field>
					<Field title="Next Step Owner">{heatmap.Next_Step_Owner__r?.Name}</Field>
				</FieldGroup>

				<FieldGroup title="Contact Information" expandable>
					<Field title="Account Name (Contact)">{heatmap.Account_Name__c}</Field>
					<Field title="Title">{heatmap.Title__c}</Field>

					<Field title="Role Category">{heatmap.Role_Category__c}</Field>
					<Field title="Segment">{heatmap.Segment__c}</Field>

					<Field title="Region">{heatmap.Region__c}</Field>
					<Field title="Subsidary">{heatmap.Subsidary__c}</Field>

					<Field title="US District">{heatmap.US_District__c}</Field>
					<Field title="Area of Interest">{heatmap.Area_of_Interest__c}</Field>

					<Field title="Industry of Focus">{heatmap.Industry_of_Focus_c__c}</Field>
					<Field title="Email">{heatmap.Email__c}</Field>

					<Field title="Reports To">{heatmap.Reports_To__c}</Field>
					<Field title="Contact Classification">{heatmap.Contact_Classification_1__c}</Field>
				</FieldGroup>

				<FieldGroup title="Record Information" expandable>
					<Field title="Created By">{heatmap.CreatedBy.Name}</Field>
					<Field title="Last Modified By">{heatmap.LastModifiedBy.Name}</Field>
				</FieldGroup>
			</div>
		</PageBase>
	)
};

export default ViewHeatmap;
